import { useMemo } from 'react';
import { Col, Skeleton } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { ReactNode } from 'react';

import type { CatalogueDesignKey } from '@eshop/designs';
import { BannersApi } from '@eshop/services/banners.api.ts';
import type { Banner } from '@eshop/types/banners.types.ts';
import type { Item } from '@eshop/types/catalogue.types.ts';
import type { Category } from '@eshop/types/categories.types.ts';
import type { CatalogueTemplate, ItemCardStyle } from '@eshop/templates/types';
import type { ItemCardSize } from '@eshop/templates';
import { selectCatalogueRootItems } from '@store/slices/eshopSlice.tsx';
import type { RootState } from '@store/store.tsx';
import type { CatalogueBannerSettings } from '@store/slices/settingsSlice.tsx';
import {
  catalogueGridColumns,
  DEFAULT_ITEM_CARD_SIZE,
  getCatalogueGridGutter,
} from '@theme/tokens/catalogue.ts';
import {
  columnsToSpans,
  resolveItemCardSize,
  type ColumnsByBp,
  type SpansByBp,
} from '../gridSize.ts';
import { ItemWithPromotions } from '../CatalogueUtils.tsx';
import { useDraftOverrides } from '@eshop/templates/context/DraftOverridesContext';

const ITEM_CARD_SIZE_TOKEN = '--catalogue-item-card-size';

interface CatalogueGridConfig {
  breakpoints: SpansByBp;
  gutter: [number, number];
  itemCardSize: ItemCardSize;
  columns: ColumnsByBp;
}

interface UseCatalogueDataArgs {
  template: CatalogueTemplate;
  design?: CatalogueDesignKey;
}

interface CatalogueDataHookResult {
  banners: Banner[];
  categorySelectedId: number | null;
  selectedCategory?: Category;
  productColumns: ReactNode[];
  skeletonColumns: ReactNode[];
  isItemLoading: boolean;
  hasMore: boolean;
  page: number;
  totalProducts: number;
  hasResults: boolean;
  items: Item[];
  gridConfig: CatalogueGridConfig;
  viewMode: ItemCardStyle;
  catalogueBannerSettings?: CatalogueBannerSettings | null;
}

const LIST_GRID_SPANS: SpansByBp = { xs: 24 };
const LIST_GRID_COLUMNS: ColumnsByBp = {
  xs: 1,
  sm: 1,
  md: 1,
  lg: 1,
  xl: 1,
  xxl: 1,
};

const buildGridConfig = (
  design: CatalogueDesignKey | undefined,
  itemCardSize: ItemCardSize,
): CatalogueGridConfig => {
  const columns = catalogueGridColumns(design, itemCardSize);
  const breakpoints = columnsToSpans(columns);

  return {
    breakpoints,
    gutter: getCatalogueGridGutter(design),
    itemCardSize,
    columns,
  };
};

export interface ResolveCatalogueGridConfigArgs {
  viewMode: ItemCardStyle;
  design?: CatalogueDesignKey;
  itemCardSize?: ItemCardSize | null;
  legacyMaxItemsPerRow?: number | null;
}

const buildListGridConfig = (design?: CatalogueDesignKey): CatalogueGridConfig => ({
  breakpoints: { ...LIST_GRID_SPANS },
  gutter: getCatalogueGridGutter(design, 'list'),
  itemCardSize: DEFAULT_ITEM_CARD_SIZE,
  columns: { ...LIST_GRID_COLUMNS },
});

export const resolveCatalogueGridConfig = ({
  viewMode,
  design,
  itemCardSize,
  legacyMaxItemsPerRow,
}: ResolveCatalogueGridConfigArgs): CatalogueGridConfig => {
  if (viewMode === 'list') {
    return buildListGridConfig(design);
  }

  const resolvedSize = resolveItemCardSize(itemCardSize, legacyMaxItemsPerRow);
  return buildGridConfig(design, resolvedSize);
};

export const useCatalogueData = ({ template, design }: UseCatalogueDataArgs): CatalogueDataHookResult => {
  const items = useSelector(selectCatalogueRootItems);
  const isItemLoading = useSelector((state: RootState) => state.eshop.isItemLoading);
  const hasMore = useSelector((state: RootState) => state.eshop.data.hasMore);
  const page = useSelector((state: RootState) => state.eshop.data.page);
  const selectedCategory = useSelector((state: RootState) => state.eshop.data.selectedCategory);
  const categorySelectedId = useSelector((state: RootState) => state.eshop.data.categorySelectedId);

  const { data: bannerData = [] } = useQuery<Banner[]>({
    queryKey: ['catalog-banners', categorySelectedId],
    queryFn: () => BannersApi.list({ page: 'catalog', category_id: categorySelectedId }),
    staleTime: 60000,
  });

  const itemsList = useMemo(() => (items ?? []) as Item[], [items]);
  const banners = useMemo(() => bannerData ?? [], [bannerData]);

  const viewMode: ItemCardStyle = template.itemCardStyle;
  const legacyMaxColumnsSetting = useSelector(
    (state: RootState) => state.settings.site?.catalogue_max_items_per_row ?? null,
  );
  const legacyItemCardSizeSetting = useSelector(
    (state: RootState) => state.settings.site?.catalogue_item_card_size ?? null,
  );

  // Read itemCardSize from template overrides (persisted in catalogue_template_overrides)
  const templateOverridesItemCardSize = useSelector((state: RootState) => {
    const overrides = state.settings.site?.catalogue_template_overrides;
    const panelTokens = overrides?.layout?.panel?.tokens;
    return panelTokens?.[ITEM_CARD_SIZE_TOKEN] ?? null;
  });

  // Draft overrides take priority during live editing
  const draftOverrides = useDraftOverrides();

  // Priority: draft overrides > template overrides > legacy setting
  const effectiveItemCardSize = useMemo(() => {
    // 1. Draft overrides (live editing)
    if (draftOverrides?.itemCardSize) {
      return draftOverrides.itemCardSize as ItemCardSize;
    }
    // 2. Template overrides (persisted in catalogue_template_overrides)
    if (templateOverridesItemCardSize) {
      return templateOverridesItemCardSize as ItemCardSize;
    }
    // 3. Legacy setting
    return legacyItemCardSizeSetting;
  }, [draftOverrides?.itemCardSize, templateOverridesItemCardSize, legacyItemCardSizeSetting]);

  const gridConfig = useMemo(
    () =>
      resolveCatalogueGridConfig({
        viewMode,
        design,
        itemCardSize: effectiveItemCardSize,
        legacyMaxItemsPerRow: legacyMaxColumnsSetting,
      }),
    [viewMode, design, effectiveItemCardSize, legacyMaxColumnsSetting],
  );

  const catalogueBannerSettings = useSelector(
    (state: RootState) => state.settings.site?.catalogue_banners ?? null,
  );

  const isListLayout = viewMode === 'list';

  const productColumns = useMemo(() => {
    return itemsList.map((item, index) => {
      if (item.promotions && item.promotions.length > 0) {
        // logInfo(`Item ${item.id} tiene ${item.promotions.length} promociones:`, item.promotions);
      }

      return (
        <Col className="catalogue-panel__col" key={item.id || index} {...gridConfig.breakpoints}>
          <ItemWithPromotions item={item} />
        </Col>
      );
    });
  }, [itemsList, gridConfig]);

  const skeletonColumns = useMemo(() => {
    const placeholders = isListLayout ? 1 : 3;

    return Array.from({ length: placeholders }).map((_, idx) => (
      <Col className="catalogue-panel__col" key={`skeleton-${idx}`} {...gridConfig.breakpoints}>
        <Skeleton.Image className="catalogue-panel__skeleton" active />
      </Col>
    ));
  }, [gridConfig, isListLayout]);

  const totalProducts = itemsList.length;
  const hasResults = totalProducts > 0;
  return {
    banners,
    categorySelectedId,
    selectedCategory,
    productColumns,
    skeletonColumns,
    isItemLoading,
    hasMore,
    page,
    totalProducts,
    hasResults,
    items: itemsList,
    gridConfig,
    viewMode,
    catalogueBannerSettings,
  };
};
